/// <amd-module name="Wit/WorkItemForm/Links/GitHub/Components/PullRequestLinkSubtitle" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { Tooltip } from "VSSUI/TooltipEx";
import { DefaultLinkTypeSubtitle } from "Wit/WorkItemLinksRegistration/Components";
import { ILinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import * as Resources from "Wit/WorkItemForm/Resources";
import { ExternalLinkedPullRequestArtifact, ExternalCheckResult } from "../GitHubDataProviderContracts";
import { GitHubCheckStatusState, GitHubCheckConclusionState } from "../../Constants";

enum GitHubCheckStatusSummary {
    InProgress = 1,
    Success,
    Failed
}

export function PullRequestLinkSubtitle({ link }: { link: ILinkDisplayData }) {
    const pullRequest = link.additionalData?.pullRequest as ExternalLinkedPullRequestArtifact | undefined;
    if (!pullRequest?.checkResults?.length) {
        return <DefaultLinkTypeSubtitle link={link} />;
    }

    return (
        <Tooltip className="commit-build-statuses fluent-icons-enabled" text={getTooltipText(pullRequest.checkResults)}>
            {renderGitHubPullReqestCheckRunIcon(pullRequest.checkResults)}
        </Tooltip>
    );
}

function getGitHubCheckStatusSummary(checkRuns?: ExternalCheckResult[]) {
    const totalCheckRunCount = checkRuns?.length;
    const successfulCheckRunCount = checkRuns?.filter(checkRun => checkRun.status?.toLowerCase() === GitHubCheckStatusState.Completed && checkRun.conclusion?.toLowerCase() === GitHubCheckConclusionState.Success).length;
    const completedCheckRunCount = checkRuns?.filter(checkRun => checkRun.status?.toLowerCase() === GitHubCheckStatusState.Completed).length;
    if (completedCheckRunCount !== totalCheckRunCount) {
        return { statusSummary: GitHubCheckStatusSummary.InProgress, totalCheckRunCount, successfulCheckRunCount };
    } else if (successfulCheckRunCount === totalCheckRunCount) {
        return { statusSummary: GitHubCheckStatusSummary.Success, totalCheckRunCount, successfulCheckRunCount };
    } else {
        return { statusSummary: GitHubCheckStatusSummary.Failed, totalCheckRunCount, successfulCheckRunCount };
    }
}

function renderGitHubPullReqestCheckRunIcon(checkRuns?: ExternalCheckResult[]): JSX.Element {
    const { statusSummary } = getGitHubCheckStatusSummary(checkRuns);
    const label = getTooltipText(checkRuns);
    switch (statusSummary) {
        case GitHubCheckStatusSummary.InProgress:
            return (
                <svg className="margin-left-4 flex-noshrink github-octicon github-color-fg-pending" aria-label={label} aria-hidden="true" height="16" viewBox="0 0 16 16" version="1.1" width="16" data-view-component="true">
                    <path d="M8 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z"></path>
                </svg>
            );
        case GitHubCheckStatusSummary.Success:
            return (
                <svg className="margin-left-4 flex-noshrink github-octicon github-color-fg-success" aria-label={label} aria-hidden="true" height="16" viewBox="0 0 16 16" version="1.1" width="16" data-view-component="true">
                    <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.751.751 0 0 1 .018-1.042.751.751 0 0 1 1.042-.018L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z"></path>
                </svg>
            );
        case GitHubCheckStatusSummary.Failed:
            return (
                <svg className="margin-left-4 flex-noshrink github-octicon github-color-fg-danger" aria-label={label} aria-hidden="true" height="16" viewBox="0 0 16 16" version="1.1" width="16" data-view-component="true">
                    <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L9.06 8l3.22 3.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L8 9.06l-3.22 3.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z"></path>
                </svg>
            );
    }
}

function getTooltipText(checkRuns?: ExternalCheckResult[]): string {
    const {statusSummary, totalCheckRunCount, successfulCheckRunCount} = getGitHubCheckStatusSummary(checkRuns);
    switch (statusSummary) {
        case GitHubCheckStatusSummary.InProgress:
            return Resources.GithubPullRequestActionsCheckInProgress;
        case GitHubCheckStatusSummary.Success:
            return totalCheckRunCount === 1 ? Resources.GithubPullRequestActionsCheckOk : format(Resources.GithubPullRequestActionsCheckPartiallyOk, successfulCheckRunCount?.toString() ?? "0", totalCheckRunCount?.toString() ?? "0");
        case GitHubCheckStatusSummary.Failed:
            return totalCheckRunCount === 1 ? Resources.GithubPullRequestActionsCheckFailed : format(Resources.GithubPullRequestActionsCheckPartiallyOk, successfulCheckRunCount?.toString() ?? "0", totalCheckRunCount?.toString() ?? "0");
    }
}